import type { Difficulty } from "./match.ts";

/**
 * Mascate (doc 03, Comércio com o mascate; doc 07, I6): um por acampamento, invulnerável, com
 * especialidade sorteada pela seed. Vende de 4 a 7 ofertas e compra de 3 a 5, com estoque de 2 a
 * 5 por oferta **por partida** (I6 manda sobre o "repor ao amanhecer" do doc 03). Paga 40 % do
 * preço de venda (para baixo, mínimo 1), então não há arbitragem; não cunha tostões.
 *
 * Os catálogos partem dos exemplos do doc 03 (preços do Arretado) e foram completados, com o mesmo
 * preço por unidade, até 8 ofertas por especialidade para caber o sorteio de 4 a 7 (premissa nossa).
 */
export type MascateSpecialty = "ferragista" | "quitandeira" | "flecheiro" | "lenheiro" | "sortido";

export interface MascateOfferDef {
  item: string;
  qty: number;
  /** Tostões no Arretado. */
  price: number;
}

const o = (item: string, qty: number, price: number): MascateOfferDef => ({ item, qty, price });

export const MASCATE_CATALOG: Record<Exclude<MascateSpecialty, "sortido">, readonly MascateOfferDef[]> = {
  flecheiro: [o("corda", 3, 6), o("flecha-pederneira", 12, 8), o("flecha-ferro", 8, 14), o("arco-ipe", 1, 30), o("flecha-pau", 20, 5), o("flecha-prata", 6, 22), o("arco-galho", 1, 10), o("pederneira", 5, 6)],
  lenheiro: [o("madeira-de-lei", 5, 10), o("carvao", 5, 8), o("tocha", 4, 5), o("resina-jatoba", 2, 12), o("madeira", 20, 6), o("palha", 10, 3), o("tora-angelim", 2, 14), o("machado-ferro", 1, 40)],
  ferragista: [o("minerio-ferro", 5, 18), o("lingote-prata", 3, 40), o("picareta-ferro", 1, 45), o("lingote-ferro", 3, 24), o("minerio-prata", 4, 26), o("pedra", 20, 4), o("facao-ferro", 1, 35), o("machado-ferro", 1, 40)],
  quitandeira: [o("tapioca", 3, 9), o("moqueca", 2, 20), o("acai", 6, 6), o("banana", 6, 5), o("peixe-assado", 3, 10), o("baiao", 2, 22), o("coco", 4, 6), o("pamonha", 2, 16)],
};

/** O Sortido sempre tem Peneira e Couro, mais ofertas sorteadas das outras listas. */
export const SORTIDO_FIXED: readonly MascateOfferDef[] = [o("peneira", 1, 25), o("couro", 3, 12)];

export const MASCATE_SPECIALTIES: readonly MascateSpecialty[] = ["ferragista", "quitandeira", "flecheiro", "lenheiro", "sortido"];
export const MASCATE_NAMES: Record<MascateSpecialty, string> = {
  ferragista: "Mercador Ferreiro",
  quitandeira: "Taverneira",
  flecheiro: "Mercador Flecheiro",
  lenheiro: "Mercador Lenheiro",
  sortido: "Mercador Sortido",
};

export const MASCATE_RULES = {
  sells: [4, 7],
  buys: [3, 5],
  stock: [2, 5],
  /** O mascate paga esta fração do preço de venda. */
  buyBack: 0.4,
  /** Distância para negociar (doc 03). */
  reach: 4,
} as const;

/** Multiplicador de preço pela dificuldade (doc 03); a Feira Livre (F24) multiplica por 0,7. */
export const MASCATE_PRICE_MUL: Record<Difficulty, number> = { tranquilo: 0.8, arretado: 1, sinistro: 1.25 };

export interface MascateOffer {
  item: string;
  qty: number;
  price: number;
  stock: number;
}

export interface MascateStock {
  specialty: MascateSpecialty;
  /** O que o mascate vende (o jogador compra). */
  sells: MascateOffer[];
  /** O que o mascate compra (o jogador vende). */
  buys: MascateOffer[];
}

const int = (rand: () => number, [min, max]: readonly [number, number]) => min + Math.floor(rand() * (max - min + 1));

/** Sorteia `count` ofertas distintas da lista (embaralhamento parcial). */
function pick<T>(list: readonly T[], count: number, rand: () => number): T[] {
  const copy = [...list];
  const out: T[] = [];
  while (out.length < count && copy.length > 0) out.push(copy.splice(Math.floor(rand() * copy.length), 1)[0] as T);
  return out;
}

/** Preço final (inteiro, mínimo 1) pela dificuldade e por multiplicadores do dia (Feira Livre). */
export const mascatePrice = (base: number, difficulty: Difficulty, extra = 1): number => Math.max(1, Math.round(base * MASCATE_PRICE_MUL[difficulty] * extra));

/** O que o mascate paga por uma oferta que vende a `sell` tostões: 40 %, para baixo, mínimo 1. */
export const buyBackPrice = (sell: number): number => Math.max(1, Math.floor(sell * MASCATE_RULES.buyBack));

/**
 * Ofertas e estoque de um mascate (pela seed dele): especialidade, de 4 a 7 vendas e de 3 a 5
 * compras, estoque de 2 a 5 por oferta. As compras saem do mesmo catálogo da especialidade.
 */
export function rollMascate(rand: () => number, difficulty: Difficulty, specialty?: MascateSpecialty): MascateStock {
  const kind = specialty ?? (MASCATE_SPECIALTIES[Math.floor(rand() * MASCATE_SPECIALTIES.length)] as MascateSpecialty);
  const others = Object.values(MASCATE_CATALOG).flat();
  const catalog = kind === "sortido" ? [...SORTIDO_FIXED, ...pick(others, 6, rand)] : [...MASCATE_CATALOG[kind]];
  const sellCount = int(rand, MASCATE_RULES.sells);
  const sellDefs = kind === "sortido" ? [...SORTIDO_FIXED, ...pick(catalog.slice(SORTIDO_FIXED.length), sellCount - SORTIDO_FIXED.length, rand)] : pick(catalog, sellCount, rand);
  const sells = sellDefs.map((d) => ({ item: d.item, qty: d.qty, price: mascatePrice(d.price, difficulty), stock: int(rand, MASCATE_RULES.stock) }));
  const buys = pick(catalog, int(rand, MASCATE_RULES.buys), rand).map((d) => ({
    item: d.item,
    qty: d.qty,
    price: buyBackPrice(mascatePrice(d.price, difficulty)),
    stock: int(rand, MASCATE_RULES.stock),
  }));
  return { specialty: kind, sells, buys };
}
